const PREFIX = 'fk_partners_';

export interface UserPreferences {
  lastSelectedExam?: string;
  lastSelectedTopic?: string;
  lastSelectedQuestionType?: string;
  lastSelectedDifficulty?: string;
  itemsPerPage?: number;
  defaultView?: 'grid' | 'list';
  defaultPaginationMode?: 'pagination' | 'infinite';
}

export const storage = {
  // Get a value from localStorage
  get<T>(key: string): T | null {
    try {
      const item = window.localStorage.getItem(PREFIX + key);
      if (!item) return null;
      try {
        return JSON.parse(item);
      } catch (error) {
        console.error('Failed to parse localStorage item:', key, error);
        return null;
      }
    } catch (error) {
      console.error(`Error reading from localStorage:`, error);
      return null;
    }
  },

  // Set a value in localStorage
  set<T>(key: string, value: T): void {
    try {
      window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing to localStorage:`, error);
    }
  },

  // Remove a value from localStorage
  remove(key: string): void {
    try {
      window.localStorage.removeItem(PREFIX + key);
    } catch (error) {
      console.error(`Error removing from localStorage:`, error);
    }
  },

  // Clear all app data from localStorage
  clear(): void {
    try {
      const keys = Object.keys(window.localStorage);
      keys.forEach((key) => {
        if (key.startsWith(PREFIX)) {
          window.localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error(`Error clearing localStorage:`, error);
    }
  },

  // User preferences
  getPreferences(): UserPreferences {
    return this.get<UserPreferences>('preferences') || {};
  },

  setPreferences(preferences: Partial<UserPreferences>): void {
    const current = this.getPreferences();
    this.set('preferences', { ...current, ...preferences });
  },

  // Remember last selected values for forms
  rememberFormValue(formName: string, fieldName: string, value: any): void {
    const key = `form_${formName}_${fieldName}`;
    this.set(key, value);
  },

  getFormValue<T>(formName: string, fieldName: string): T | null {
    const key = `form_${formName}_${fieldName}`;
    return this.get<T>(key);
  },

  // Recent searches
  addRecentSearch(query: string): void {
    const searches = this.getRecentSearches();
    const updated = [query, ...searches.filter((s) => s !== query)].slice(0, 10);
    this.set('recent_searches', updated);
  },

  getRecentSearches(): string[] {
    return this.get<string[]>('recent_searches') || [];
  },

  // Recently viewed items
  addRecentlyViewed(type: string, id: string, label: string): void {
    const key = `recent_${type}`;
    const items = this.get<Array<{ id: string; label: string; timestamp: number }>>(key) || [];
    const updated = [
      { id, label, timestamp: Date.now() },
      ...items.filter((item) => item.id !== id),
    ].slice(0, 10);
    this.set(key, updated);
  },

  getRecentlyViewed(type: string): Array<{ id: string; label: string; timestamp: number }> {
    const key = `recent_${type}`;
    return this.get(key) || [];
  },

  // Favorites/bookmarks
  toggleFavorite(type: string, id: string): void {
    const key = `favorites_${type}`;
    const favorites = this.get<string[]>(key) || [];
    if (favorites.includes(id)) {
      this.set(key, favorites.filter((fav) => fav !== id));
    } else {
      this.set(key, [...favorites, id]);
    }
  },

  isFavorite(type: string, id: string): boolean {
    const key = `favorites_${type}`;
    const favorites = this.get<string[]>(key) || [];
    return favorites.includes(id);
  },

  getFavorites(type: string): string[] {
    const key = `favorites_${type}`;
    return this.get<string[]>(key) || [];
  },

  // Draft saving
  saveDraft(type: string, id: string, data: any): void {
    const key = `draft_${type}_${id}`;
    this.set(key, {
      data,
      timestamp: Date.now(),
    });
  },

  getDraft<T>(type: string, id: string): { data: T; timestamp: number } | null {
    const key = `draft_${type}_${id}`;
    return this.get(key);
  },

  clearDraft(type: string, id: string): void {
    const key = `draft_${type}_${id}`;
    this.remove(key);
  },

  // Column preferences for tables
  setColumnPreferences(tableId: string, columns: string[]): void {
    this.set(`table_columns_${tableId}`, columns);
  },

  getColumnPreferences(tableId: string): string[] | null {
    return this.get(`table_columns_${tableId}`);
  },

  // Filter presets
  saveFilterPreset(pageId: string, presetName: string, filters: any): void {
    const key = `filter_presets_${pageId}`;
    const presets = this.get<Record<string, any>>(key) || {};
    presets[presetName] = filters;
    this.set(key, presets);
  },

  getFilterPresets(pageId: string): Record<string, any> {
    const key = `filter_presets_${pageId}`;
    return this.get(key) || {};
  },

  deleteFilterPreset(pageId: string, presetName: string): void {
    const key = `filter_presets_${pageId}`;
    const presets = this.get<Record<string, any>>(key) || {};
    delete presets[presetName];
    this.set(key, presets);
  },
};
